import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";

const BASE_URL = process.env.REACT_APP_BE_API_URL ?? "";

// ─── Error personalizado ──────────────────────────────────────────────────────

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// ─── Instancia de Axios ───────────────────────────────────────────────────────

const axiosInstance: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// ─── Interceptor de request (ej: auth token) ─────────────────────────────────

axiosInstance.interceptors.request.use((config: any) => {
  const token = localStorage.getItem("token");
  const reqUrl = (config.url || "").toString();

  // Adjuntar token sólo para rutas protegidas
  const isAdminRoute =
    reqUrl.startsWith("/admin") || /\/admin(\/|$)/.test(reqUrl);
  const isAuthProtectedRoute =
    reqUrl.startsWith("/auth") && !reqUrl.startsWith("/auth/login");

  if ((isAdminRoute || isAuthProtectedRoute) && token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Si los datos son FormData, eliminamos Content-Type para que axios establezca
  // automáticamente multipart/form-data con el boundary correcto.
  if (config.data instanceof FormData) {
    delete config.headers["Content-Type"];
  }

  return config;
});

// ─── Interceptor de errores ───────────────────────────────────────────────────

axiosInstance.interceptors.response.use(
  (response: any) => response,
  (error: AxiosError<any>) => {
    const status = error.response?.status ?? 500;

    // En caso de no estar autorizado (sesión expirada o inválida)
    if (status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      // Redirigir al login si no estamos ya en la página de login
      if (!window.location.pathname.includes("/admin/login")) {
        window.location.href = "/admin/login";
      }
    }

    const message =
      error.response?.data?.message ??
      error.response?.data?.error ??
      error.message ??
      "Unknown error";

    return Promise.reject(new ApiError(status, message));
  },
);

// ─── Helper genérico ──────────────────────────────────────────────────────────

async function request<T>(config: AxiosRequestConfig): Promise<T> {
  const response = await axiosInstance.request<T>(config);
  return response.data;
}

// ─── Métodos exportados ───────────────────────────────────────────────────────

export const api = {
  get: <T>(endpoint: string, config?: AxiosRequestConfig) =>
    request<T>({
      url: endpoint,
      method: "GET",
      ...config,
    }),

  post: <T>(endpoint: string, body?: unknown, config?: AxiosRequestConfig) =>
    request<T>({
      url: endpoint,
      method: "POST",
      data: body,
      ...config,
    }),

  put: <T>(endpoint: string, body?: unknown, config?: AxiosRequestConfig) =>
    request<T>({
      url: endpoint,
      method: "PUT",
      data: body,
      ...config,
    }),

  patch: <T>(endpoint: string, body?: unknown, config?: AxiosRequestConfig) =>
    request<T>({
      url: endpoint,
      method: "PATCH",
      data: body,
      ...config,
    }),

  delete: <T = void>(endpoint: string, config?: AxiosRequestConfig) =>
    request<T>({
      url: endpoint,
      method: "DELETE",
      ...config,
    }),
};
